import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL, COMPANY } from "@/lib/siteData";

/**
 * Cinematic loading screen. Logo reveal + animated progress + drifting
 * gold particles. Auto-hides on window `load` or after a soft cap.
 */
export default function Loader() {
    const [done, setDone] = useState(false);
    const [progress, setProgress] = useState(0);
    const startedAt = useRef(Date.now());

    useEffect(() => {
        let raf;
        const cap = 2400;
        const tick = () => {
            const elapsed = Date.now() - startedAt.current;
            const soft = Math.min(1, elapsed / cap);
            const eased = 1 - Math.pow(1 - soft, 2.4);
            setProgress(Math.floor(eased * 100));
            if (soft < 1 && !done) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        const finish = () => {
            setProgress(100);
            setTimeout(() => setDone(true), 480);
        };
        window.addEventListener("load", finish);
        const cap2 = setTimeout(finish, cap + 200);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(cap2);
            window.removeEventListener("load", finish);
        };
    }, [done]);

    // Lock scroll while loading
    useEffect(() => {
        if (done) {
            document.body.style.overflow = "";
        } else {
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [done]);

    return (
        <AnimatePresence>
            {!done && (
                <motion.div
                    key="loader"
                    data-testid="loader"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
                    className="fixed inset-0 z-[10000] bg-[#050505] text-white flex items-center justify-center overflow-hidden"
                >
                    {/* Particles */}
                    <Particles />

                    {/* Diagonal accent lines */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/3 -left-40 h-px w-[60%] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent rotate-[8deg]" />
                        <div className="absolute bottom-1/4 -right-40 h-px w-[60%] bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent -rotate-[8deg]" />
                    </div>

                    <div className="relative z-10 w-full max-w-md px-8 text-center">
                        <motion.img
                            src={LOGO_URL}
                            alt={COMPANY.name}
                            initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
                            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                            transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
                            className="h-24 w-24 md:h-28 md:w-28 object-contain mx-auto"
                        />

                        {/* Curtain reveal for wordmark */}
                        <div className="mt-6 overflow-hidden">
                            <motion.div
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.9, delay: 0.4, ease: [0.65, 0, 0.35, 1] }}
                                className="font-display text-3xl md:text-4xl tracking-tight leading-none"
                            >
                                CHANDAK <span style={{ color: "#D4AF37" }}>BuildVision</span>
                            </motion.div>
                        </div>
                        <div className="mt-2 overflow-hidden">
                            <motion.div
                                initial={{ y: "110%", opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.7, delay: 0.8 }}
                                className="text-[10px] uppercase tracking-[0.42em] text-white/60"
                            >
                                Feel Before You Build
                            </motion.div>
                        </div>

                        {/* Progress */}
                        <div className="mt-14 flex items-center justify-between text-[10px] uppercase tracking-[0.32em] text-white/50">
                            <span>Loading Studio</span>
                            <span data-testid="loader-progress">{progress}%</span>
                        </div>
                        <div className="mt-3 h-px w-full bg-white/10 overflow-hidden">
                            <motion.div
                                className="h-full bg-[#D4AF37]"
                                style={{ width: `${progress}%` }}
                                transition={{ ease: "easeOut" }}
                            />
                        </div>
                    </div>

                    {/* Bottom hairline */}
                    <div className="absolute bottom-8 inset-x-0 flex items-center justify-between px-8 text-[10px] uppercase tracking-[0.32em] text-white/40">
                        <span>Hyderabad · India</span>
                        <span>MMXXVI</span>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Particles() {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const DPR = Math.min(window.devicePixelRatio || 1, 2);

        const resize = () => {
            canvas.width = window.innerWidth * DPR;
            canvas.height = window.innerHeight * DPR;
            canvas.style.width = window.innerWidth + "px";
            canvas.style.height = window.innerHeight + "px";
        };
        resize();

        const N = 60;
        const particles = Array.from({ length: N }).map(() => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: (Math.random() * 1.6 + 0.4) * DPR,
            vx: (Math.random() - 0.5) * 0.25 * DPR,
            vy: (Math.random() - 0.5) * 0.25 * DPR,
            a: Math.random() * 0.4 + 0.15,
        }));

        let raf;
        const step = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach((p) => {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
                if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(212,175,55,${p.a})`;
                ctx.fill();
            });
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        window.addEventListener("resize", resize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);
    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full opacity-90"
        />
    );
}
